/**
 * Parsing and building of streaming-service links. Pure functions shared by the web app, the API
 * and a future mobile app. Nothing here touches the network.
 */

const SPOTIFY_ID = /^[A-Za-z0-9]{22}$/;
const LINK_START =
  /^(?:https?:\/\/|spotify:|(?:open|play)\.spotify\.com\/|spotify\.link\/|(?:geo\.)?music\.apple\.com\/|itunes\.apple\.com\/)/i;
const SPOTIFY_ENTITIES = [
  'track',
  'album',
  'playlist',
  'artist',
  'episode',
  'show',
  'user',
  'audiobook',
  'chapter',
  'prerelease',
] as const;
export type SpotifyEntity = (typeof SPOTIFY_ENTITIES)[number] | 'other';

export type SpotifyLinkResult =
  | { kind: 'track'; id: string; canonicalUrl: string }
  /** spotify.link short links must be resolved by the server (redirect to open.spotify.com). */
  | { kind: 'short'; url: string }
  | { kind: 'unsupported'; entity: Exclude<SpotifyEntity, 'track'> }
  | { kind: 'invalid' };

/**
 * Pulls the first link out of pasted text ("Listen to this: https://…"). A link must start a
 * whitespace-separated token (after optional opening brackets/quotes), so "ftp://…" or
 * "javascript:…//open.spotify.com/…" are not treated as links.
 */
export function extractLink(input: string): string | null {
  for (const token of input.trim().split(/\s+/)) {
    const candidate = token.replace(/^[(<["'“‘]+/, '');
    if (LINK_START.test(candidate)) {
      // Trim trailing punctuation people often paste along with the link.
      return candidate.replace(/[)\]}>.,;!?"'”’]+$/, '');
    }
  }
  return null;
}

function toUrl(raw: string): URL | null {
  const withScheme = /^[a-z][a-z0-9+.-]*:\/\//i.test(raw) ? raw : `https://${raw}`;
  try {
    return new URL(withScheme);
  } catch {
    return null;
  }
}

export function spotifyTrackUrl(id: string): string {
  return `https://open.spotify.com/track/${id}`;
}

/**
 * Normalises every Spotify link shape to a canonical track URL:
 * open.spotify.com/track/{id}, with ?si= or /intl-xx/ segments, /embed/track/{id},
 * play.spotify.com, spotify:track:{id} URIs, and spotify.link short links (kind "short").
 * Albums, playlists, artists and podcasts are reported as unsupported.
 */
export function parseSpotifyLink(input: string): SpotifyLinkResult {
  const raw = extractLink(input);
  if (!raw) return { kind: 'invalid' };

  if (/^spotify:/i.test(raw)) {
    const parts = raw.split(':');
    const entity = (parts[1] ?? '').toLowerCase();
    const id = parts[2] ?? '';
    if (entity === 'track') {
      return SPOTIFY_ID.test(id)
        ? { kind: 'track', id, canonicalUrl: spotifyTrackUrl(id) }
        : { kind: 'invalid' };
    }
    return { kind: 'unsupported', entity: asEntity(entity) };
  }

  const url = toUrl(raw);
  if (!url || (url.protocol !== 'https:' && url.protocol !== 'http:')) return { kind: 'invalid' };
  const host = url.hostname.toLowerCase();

  if (host === 'spotify.link') {
    return /^\/[A-Za-z0-9]+\/?$/.test(url.pathname)
      ? { kind: 'short', url: `https://spotify.link${url.pathname.replace(/\/$/, '')}` }
      : { kind: 'invalid' };
  }
  if (host !== 'open.spotify.com' && host !== 'play.spotify.com') return { kind: 'invalid' };

  const segments = url.pathname.split('/').filter(Boolean);
  // Drop locale (/intl-de/) and embed (/embed/) prefixes.
  while (
    segments[0] &&
    (/^intl-[a-z]{2}(?:[-_][a-z0-9]{2,4})?$/i.test(segments[0]) || segments[0] === 'embed')
  ) {
    segments.shift();
  }
  const [entity = '', id = ''] = segments;
  if (entity.toLowerCase() === 'track') {
    return SPOTIFY_ID.test(id)
      ? { kind: 'track', id, canonicalUrl: spotifyTrackUrl(id) }
      : { kind: 'invalid' };
  }
  if (!entity) return { kind: 'invalid' };
  return { kind: 'unsupported', entity: asEntity(entity.toLowerCase()) };
}

function asEntity(value: string): Exclude<SpotifyEntity, 'track'> {
  const known = (SPOTIFY_ENTITIES as readonly string[]).includes(value) ? value : 'other';
  return (known === 'track' ? 'other' : known) as Exclude<SpotifyEntity, 'track'>;
}

export type AppleMusicLinkResult =
  | { kind: 'track'; trackId: number; country: string | null }
  | { kind: 'unsupported'; entity: 'album' | 'playlist' | 'artist' | 'music-video' | 'other' }
  | { kind: 'invalid' };

const APPLE_HOSTS = new Set([
  'music.apple.com',
  'geo.music.apple.com',
  'itunes.apple.com',
  'geo.itunes.apple.com',
]);

/**
 * Apple Music / iTunes song links: /{cc}/album/{slug}/{albumId}?i={trackId} and
 * /{cc}/song/{slug}/{trackId} (country and slug optional).
 */
export function parseAppleMusicLink(input: string): AppleMusicLinkResult {
  const raw = extractLink(input);
  if (!raw) return { kind: 'invalid' };
  const url = toUrl(raw);
  if (!url || !APPLE_HOSTS.has(url.hostname.toLowerCase())) return { kind: 'invalid' };

  const segments = url.pathname.split('/').filter(Boolean);
  let country: string | null = null;
  if (segments[0] && /^[a-z]{2}$/i.test(segments[0])) country = segments.shift()!.toLowerCase();
  const type = (segments[0] ?? '').toLowerCase();
  const last = segments[segments.length - 1] ?? '';
  const numeric = (v: string | null | undefined) => (v && /^\d{1,15}$/.test(v) ? Number(v) : null);

  const trackParam = numeric(url.searchParams.get('i'));
  if (type === 'album' && trackParam) return { kind: 'track', trackId: trackParam, country };
  if (type === 'song') {
    const id = numeric(last.replace(/^id/, ''));
    return id ? { kind: 'track', trackId: id, country } : { kind: 'invalid' };
  }
  if (type === 'album') return { kind: 'unsupported', entity: 'album' };
  if (type === 'playlist') return { kind: 'unsupported', entity: 'playlist' };
  if (type === 'artist') return { kind: 'unsupported', entity: 'artist' };
  if (type === 'music-video') return { kind: 'unsupported', entity: 'music-video' };
  return type ? { kind: 'unsupported', entity: 'other' } : { kind: 'invalid' };
}

/** Which service a pasted link belongs to, if any. */
export function detectLinkService(input: string): 'spotify' | 'apple' | null {
  if (parseSpotifyLink(input).kind !== 'invalid') return 'spotify';
  if (parseAppleMusicLink(input).kind !== 'invalid') return 'apple';
  return null;
}

export function spotifySearchUrl(title: string, artist: string): string {
  return `https://open.spotify.com/search/${encodeURIComponent(`${title} ${artist}`.trim())}`;
}

export function spotifyEmbedUrl(id: string): string {
  return `https://open.spotify.com/embed/track/${id}`;
}

export function youtubeMusicSearchUrl(title: string, artist: string): string {
  return `https://music.youtube.com/search?q=${encodeURIComponent(`${title} ${artist}`.trim())}`;
}

/** iTunes artwork URLs embed their size; ask Apple's CDN for a larger rendition. */
export function upscaleArtwork(url: string | null | undefined, size = 600): string | null {
  if (!url) return null;
  return url.replace(/\/\d+x\d+bb(\.[a-z]+)$/i, `/${size}x${size}bb$1`);
}

/**
 * Normalises a song title for fuzzy comparison: lowercase, no accents, no "(Remastered 2011)",
 * "- Live", "feat. X" decorations, no punctuation.
 */
export function normalizeTitle(title: string): string {
  return title
    .normalize('NFKD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .replace(/\s[-–—]\s.*$/, '')
    .replace(/[([{][^)\]}]*[)\]}]/g, ' ')
    .replace(/\b(feat|ft|featuring|with)\.?\s.*$/, '')
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .trim();
}

/** True when two titles are "the same song" after normalisation (token overlap ≥ 0.6). */
export function titlesMatch(a: string, b: string): boolean {
  const na = normalizeTitle(a);
  const nb = normalizeTitle(b);
  if (!na || !nb) return false;
  if (na === nb || na.includes(nb) || nb.includes(na)) return true;
  const ta = new Set(na.split(' '));
  const tb = new Set(nb.split(' '));
  const shared = [...ta].filter((t) => tb.has(t)).length;
  return shared / Math.max(ta.size, tb.size) >= 0.6;
}
